import { useCallback, useEffect, useRef, useState } from "react"
import Hls from "hls.js"
import api from "../../lib/api"
import { getToken } from "../../lib/auth"
import { VideoIcon } from "../../icons"
import type { VideoStatus } from "../../types/video"
import { LoadState } from "@/components/ui/LoadState"

type VideoMeta = {
  status: VideoStatus
  hls_url: string
  duration: number
}

const STATUS_MESSAGES: Record<Exclude<VideoStatus, "ready">, string> = {
  uploading: "El video todavía se está subiendo — la vista previa estará disponible cuando termine de procesarse.",
  queued: "El video está en cola, esperando su turno para procesarse — la vista previa estará disponible en unos minutos.",
  processing: "El video se está procesando — la vista previa estará disponible en unos minutos.",
  transcribing: "El video ya se procesó y se está transcribiendo — la vista previa estará disponible en unos minutos.",
  error: "El video quedó en estado de error durante el procesamiento — no hay vista previa disponible.",
}

const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 2]

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "00:00"
  const total = Math.floor(seconds)
  const minutes = Math.floor(total / 60)
  const secs = total % 60
  return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
}

// Iconos propios (24x24, trazo redondeado uniforme) para no depender de una
// librería de íconos nueva solo para este reproductor.
function PlayIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor">
      <path d="M8 5.5v13a1 1 0 0 0 1.5.87l11-6.5a1 1 0 0 0 0-1.74l-11-6.5A1 1 0 0 0 8 5.5Z" />
    </svg>
  )
}
function PauseIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor">
      <rect x="7" y="5" width="4" height="14" rx="1" />
      <rect x="13" y="5" width="4" height="14" rx="1" />
    </svg>
  )
}
function iconProps() {
  return { fill: "none", stroke: "currentColor", strokeWidth: 1.75, strokeLinecap: "round" as const, strokeLinejoin: "round" as const }
}
function VolumeIcon({ level, className }: { level: "off" | "low" | "high"; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} {...iconProps()}>
      <path d="M11 5 6 9H2v6h4l5 4V5Z" />
      {level === "off" && (
        <>
          <path d="m17 9 5 6" />
          <path d="m22 9-5 6" />
        </>
      )}
      {level === "low" && <path d="M15.5 8.5a5 5 0 0 1 0 7" />}
      {level === "high" && (
        <>
          <path d="M15.5 8.5a5 5 0 0 1 0 7" />
          <path d="M19 5a10 10 0 0 1 0 14" />
        </>
      )}
    </svg>
  )
}
function SpeedIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} {...iconProps()}>
      <circle cx="7" cy="7" r="1.4" fill="currentColor" stroke="none" />
      <path d="M4 7h1.5M11.5 7H20" />
      <circle cx="15" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <path d="M4 12h9.5M16.5 12H20" />
      <circle cx="10" cy="17" r="1.4" fill="currentColor" stroke="none" />
      <path d="M4 17h4.5M11.5 17H20" />
    </svg>
  )
}
function CheckIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} {...iconProps()}>
      <path d="m20 6-11 11-5-5" />
    </svg>
  )
}
function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} {...iconProps()}>
      <path d="m15 18-6-6 6-6" />
    </svg>
  )
}
function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} {...iconProps()}>
      <path d="m9 18 6-6-6-6" />
    </svg>
  )
}
function QualityIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} {...iconProps()}>
      <rect x="3" y="7" width="18" height="12" rx="2" />
      <path d="M8 11v4M12 9v6M16 12v3" />
    </svg>
  )
}
function MaximizeIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} {...iconProps()}>
      <path d="M4 9V4h5" />
      <path d="M20 9V4h-5" />
      <path d="M4 15v5h5" />
      <path d="M20 15v5h-5" />
    </svg>
  )
}
function MinimizeIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} {...iconProps()}>
      <path d="M9 4v5H4" />
      <path d="M15 4v5h5" />
      <path d="M9 20v-5H4" />
      <path d="M15 20v-5h5" />
    </svg>
  )
}
function PiPIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} {...iconProps()}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <rect x="12" y="12" width="7" height="5" rx="1" fill="currentColor" stroke="none" />
    </svg>
  )
}

/**
 * Previsualización de un video ya subido, para que un admin pueda confirmar
 * de un vistazo cuál archivo es (el título/descripción a veces no alcanzan).
 */
export default function VideoPreviewPlayer({ videoId, status }: { videoId: string; status: VideoStatus }) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const hlsRef = useRef<Hls | null>(null)
  const scrubberRef = useRef<HTMLDivElement | null>(null)

  const [meta, setMeta] = useState<VideoMeta | null>(null)
  const [loading, setLoading] = useState(status === "ready")
  const [error, setError] = useState<string | null>(null)

  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [bufferedPercent, setBufferedPercent] = useState(0)
  const [volume, setVolume] = useState(1)
  const [isMuted, setIsMuted] = useState(false)
  const [playbackRate, setPlaybackRate] = useState(1)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isPiP, setIsPiP] = useState(false)
  const [pipSupported, setPipSupported] = useState(false)
  const [settingsPane, setSettingsPane] = useState<"closed" | "main" | "speed" | "quality">("closed")
  const [levels, setLevels] = useState<Array<{ index: number; label: string }>>([])
  const [selectedLevel, setSelectedLevel] = useState(-1)

  useEffect(() => {
    if (status !== "ready") return
    let mounted = true
    setLoading(true)
    setError(null)
    api
      .get(`/academy/videos/${videoId}/meta`)
      .then((res) => {
        if (!mounted) return
        setMeta(res.data?.data ?? null)
        setDuration(res.data?.data?.duration ?? 0)
      })
      .catch(() => {
        if (mounted) setError("No se pudo cargar la vista previa de este video.")
      })
      .finally(() => {
        if (mounted) setLoading(false)
      })
    return () => {
      mounted = false
    }
  }, [videoId, status])

  useEffect(() => {
    const video = videoRef.current
    if (!video || !meta?.hls_url) return

    setLevels([])
    setSelectedLevel(-1)

    if (hlsRef.current) {
      hlsRef.current.destroy()
      hlsRef.current = null
    }

    if (Hls.isSupported()) {
      const instance = new Hls({
        xhrSetup: (xhr) => {
          const token = getToken()
          if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`)
        },
      })
      instance.attachMedia(video)
      instance.on(Hls.Events.MEDIA_ATTACHED, () => instance.loadSource(meta.hls_url))
      instance.on(Hls.Events.MANIFEST_PARSED, () => {
        setLevels(
          instance.levels.map((level, index) => ({
            index,
            label: level.height ? `${level.height}p` : `Nivel ${index + 1}`,
          }))
        )
      })
      instance.on(Hls.Events.LEVEL_SWITCHED, (_evt, data) => setSelectedLevel(data.level))
      instance.on(Hls.Events.ERROR, (_evt, data) => {
        if (data.fatal) setError("La reproducción se interrumpió — intenta recargar la página.")
      })
      hlsRef.current = instance
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = meta.hls_url
    } else {
      setError("Este navegador no soporta reproducción HLS.")
    }

    return () => {
      hlsRef.current?.destroy()
      hlsRef.current = null
    }
  }, [meta?.hls_url])

  useEffect(() => {
    setPipSupported(typeof document !== "undefined" && Boolean(document.pictureInPictureEnabled))
  }, [])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const onPlay = () => setIsPlaying(true)
    const onPause = () => setIsPlaying(false)
    const onVolumeChange = () => {
      setVolume(video.volume)
      setIsMuted(video.muted)
    }
    const onRateChange = () => setPlaybackRate(video.playbackRate)
    const onEnterPiP = () => setIsPiP(true)
    const onLeavePiP = () => setIsPiP(false)
    const onTimeUpdate = () => {
      const dur = Number.isFinite(video.duration) ? video.duration : 0
      setCurrentTime(video.currentTime || 0)
      if (dur > 0) setDuration(dur)
      if (video.buffered.length > 0 && dur > 0) {
        setBufferedPercent(Math.min(100, (video.buffered.end(video.buffered.length - 1) / dur) * 100))
      }
    }

    video.addEventListener("play", onPlay)
    video.addEventListener("pause", onPause)
    video.addEventListener("volumechange", onVolumeChange)
    video.addEventListener("ratechange", onRateChange)
    video.addEventListener("enterpictureinpicture", onEnterPiP)
    video.addEventListener("leavepictureinpicture", onLeavePiP)
    video.addEventListener("timeupdate", onTimeUpdate)
    video.addEventListener("progress", onTimeUpdate)
    video.addEventListener("loadedmetadata", onTimeUpdate)

    return () => {
      video.removeEventListener("play", onPlay)
      video.removeEventListener("pause", onPause)
      video.removeEventListener("volumechange", onVolumeChange)
      video.removeEventListener("ratechange", onRateChange)
      video.removeEventListener("enterpictureinpicture", onEnterPiP)
      video.removeEventListener("leavepictureinpicture", onLeavePiP)
      video.removeEventListener("timeupdate", onTimeUpdate)
      video.removeEventListener("progress", onTimeUpdate)
      video.removeEventListener("loadedmetadata", onTimeUpdate)
    }
  }, [meta?.hls_url])

  useEffect(() => {
    const onFullscreenChange = () => setIsFullscreen(document.fullscreenElement === containerRef.current)
    document.addEventListener("fullscreenchange", onFullscreenChange)
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange)
  }, [])

  const togglePlay = useCallback(async () => {
    const video = videoRef.current
    if (!video) return
    if (video.paused) await video.play()
    else video.pause()
  }, [])

  const toggleMute = useCallback(() => {
    const video = videoRef.current
    if (!video) return
    video.muted = !(video.muted || video.volume === 0)
    if (!video.muted && video.volume === 0) video.volume = 1
  }, [])

  const toggleFullscreen = useCallback(async () => {
    const container = containerRef.current
    if (!container) return
    if (document.fullscreenElement) await document.exitFullscreen()
    else await container.requestFullscreen()
  }, [])

  const togglePiP = useCallback(async () => {
    const video = videoRef.current
    if (!video) return
    try {
      if (document.pictureInPictureElement) await document.exitPictureInPicture()
      else await video.requestPictureInPicture()
    } catch (err) {
      console.error("PiP error:", err)
    }
  }, [])

  const onScrub = useCallback(
    (ratio: number) => {
      const video = videoRef.current
      if (!video || !duration) return
      video.currentTime = duration * ratio
      setCurrentTime(video.currentTime)
    },
    [duration]
  )

  if (status !== "ready") {
    return (
      <div className="flex aspect-video w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-gray-200 bg-gray-50/60 px-6 text-center text-gray-400 dark:border-gray-700 dark:bg-white/[0.02]">
        <VideoIcon className="size-8" />
        <p className="text-xs">{STATUS_MESSAGES[status]}</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex aspect-video w-full items-center justify-center rounded-xl bg-black text-gray-400">
        <LoadState compact label="Cargando vista previa..." />
      </div>
    )
  }

  if (error || !meta) {
    return (
      <div className="flex aspect-video w-full items-center justify-center rounded-xl bg-black px-6 text-center text-xs text-error-300">
        {error ?? "No se pudo inicializar la vista previa."}
      </div>
    )
  }

  const volumeLevel = isMuted || volume === 0 ? "off" : volume < 0.5 ? "low" : "high"

  return (
    <div ref={containerRef} className="group/preview relative aspect-video w-full overflow-hidden rounded-xl bg-black">
      <video
        ref={videoRef}
        className="h-full w-full object-contain"
        playsInline
        onClick={() => void togglePlay()}
      />

      {!isPlaying && (
        <button
          type="button"
          onClick={() => void togglePlay()}
          aria-label="Reproducir"
          className="absolute inset-0 z-10 flex items-center justify-center"
        >
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-black/50 backdrop-blur-sm transition-transform duration-200 ease-out group-hover/preview:scale-105">
            <PlayIcon className="h-6 w-6 translate-x-0.5 text-white" />
          </span>
        </button>
      )}

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/85 to-transparent" />

      <div className="absolute inset-x-0 bottom-0 z-20 flex flex-col gap-1.5 px-3 pb-2.5">
        <div
          ref={scrubberRef}
          role="slider"
          tabIndex={0}
          aria-label="Progreso del video"
          aria-valuemin={0}
          aria-valuemax={duration || 0}
          aria-valuenow={currentTime}
          className="group/scrub relative flex h-3.5 cursor-pointer items-center"
          onClick={(event) => {
            const rect = scrubberRef.current?.getBoundingClientRect()
            if (!rect) return
            onScrub(Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width)))
          }}
          onKeyDown={(event) => {
            const video = videoRef.current
            if (!video || !duration) return
            if (event.key === "ArrowRight") {
              event.preventDefault()
              video.currentTime = Math.min(duration, video.currentTime + 5)
            } else if (event.key === "ArrowLeft") {
              event.preventDefault()
              video.currentTime = Math.max(0, video.currentTime - 5)
            }
          }}
        >
          <div className="absolute left-0 right-0 h-1 rounded-full bg-white/25 transition-all duration-150 group-hover/scrub:h-1.5" />
          <div
            className="absolute left-0 h-1 rounded-full bg-white/40 transition-all duration-150 group-hover/scrub:h-1.5"
            style={{ width: `${bufferedPercent}%` }}
          />
          <div
            className="absolute left-0 h-1 rounded-full bg-error-600 transition-all duration-150 group-hover/scrub:h-1.5"
            style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
          />
          <div
            className="absolute h-2.5 w-2.5 -translate-x-1/2 scale-0 rounded-full bg-error-600 shadow transition-transform duration-150 group-hover/scrub:scale-100"
            style={{ left: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
          />
        </div>

        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => void togglePlay()}
              aria-label={isPlaying ? "Pausar" : "Reproducir"}
              className="flex h-7 w-7 items-center justify-center rounded-full text-white transition hover:bg-white/10"
            >
              {isPlaying ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4 translate-x-0.5" />}
            </button>

            <div className="group/volume flex items-center gap-1.5">
              <button
                type="button"
                onClick={toggleMute}
                aria-label={isMuted ? "Activar sonido" : "Silenciar"}
                className="flex h-7 w-7 items-center justify-center rounded-full text-white transition hover:bg-white/10"
              >
                <VolumeIcon level={volumeLevel} className="h-4 w-4" />
              </button>
              <input
                type="range"
                min={0}
                max={1}
                step={0.01}
                value={isMuted ? 0 : volume}
                onChange={(event) => {
                  const video = videoRef.current
                  if (!video) return
                  const value = Number.parseFloat(event.target.value)
                  video.volume = value
                  video.muted = value === 0
                }}
                aria-label="Volumen"
                className="h-1 w-0 origin-left cursor-pointer overflow-hidden rounded-full bg-white/25 opacity-0 transition-all duration-200 group-hover/volume:w-14 group-hover/volume:opacity-100 accent-white"
              />
            </div>

            <span className="text-[11px] tabular-nums text-white/90">
              {formatTime(currentTime)} / {formatTime(duration)}
            </span>
          </div>

          <div className="flex items-center gap-0.5">
            <div className="relative">
              <button
                type="button"
                onClick={() => setSettingsPane((prev) => (prev === "closed" ? "main" : "closed"))}
                aria-label="Ajustes"
                className="flex h-7 w-7 items-center justify-center rounded-full text-white transition hover:bg-white/10"
              >
                <SpeedIcon className="h-4 w-4" />
              </button>
              {settingsPane !== "closed" && (
                <>
                  <button
                    type="button"
                    aria-label="Cerrar ajustes"
                    onClick={() => setSettingsPane("closed")}
                    className="fixed inset-0 z-20 cursor-default"
                  />
                  <div className="absolute bottom-9 right-0 z-30 w-44 overflow-hidden rounded-lg border border-white/10 bg-black/95 text-xs text-white shadow-2xl">
                    {settingsPane === "main" && (
                      <div className="py-1">
                        <button
                          type="button"
                          onClick={() => setSettingsPane("speed")}
                          className="flex w-full items-center justify-between px-3 py-2 transition hover:bg-white/10"
                        >
                          <span className="flex items-center gap-1.5"><SpeedIcon className="h-3.5 w-3.5" /> Velocidad</span>
                          <span className="flex items-center gap-1 text-white/60">
                            {playbackRate === 1 ? "Normal" : `${playbackRate}x`}
                            <ChevronRightIcon className="h-3.5 w-3.5" />
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setSettingsPane("quality")}
                          className="flex w-full items-center justify-between px-3 py-2 transition hover:bg-white/10"
                        >
                          <span className="flex items-center gap-1.5"><QualityIcon className="h-3.5 w-3.5" /> Calidad</span>
                          <span className="flex items-center gap-1 text-white/60">
                            {selectedLevel === -1 ? "Auto" : levels.find((l) => l.index === selectedLevel)?.label ?? "Auto"}
                            <ChevronRightIcon className="h-3.5 w-3.5" />
                          </span>
                        </button>
                      </div>
                    )}

                    {settingsPane === "speed" && (
                      <div className="py-1">
                        <button
                          type="button"
                          onClick={() => setSettingsPane("main")}
                          className="flex w-full items-center gap-1.5 border-b border-white/10 px-3 py-2 font-medium transition hover:bg-white/10"
                        >
                          <ChevronLeftIcon className="h-3.5 w-3.5" /> Velocidad
                        </button>
                        {PLAYBACK_RATES.map((rate) => (
                          <button
                            key={rate}
                            type="button"
                            onClick={() => {
                              const video = videoRef.current
                              if (video) video.playbackRate = rate
                              setSettingsPane("closed")
                            }}
                            className="flex w-full items-center justify-between px-3 py-1.5 transition hover:bg-white/10"
                          >
                            <span>{rate === 1 ? "Normal" : `${rate}x`}</span>
                            {playbackRate === rate && <CheckIcon className="h-3.5 w-3.5 text-error-600 dark:text-error-400" />}
                          </button>
                        ))}
                      </div>
                    )}

                    {settingsPane === "quality" && (
                      <div className="py-1">
                        <button
                          type="button"
                          onClick={() => setSettingsPane("main")}
                          className="flex w-full items-center gap-1.5 border-b border-white/10 px-3 py-2 font-medium transition hover:bg-white/10"
                        >
                          <ChevronLeftIcon className="h-3.5 w-3.5" /> Calidad
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedLevel(-1)
                            if (hlsRef.current) hlsRef.current.currentLevel = -1
                            setSettingsPane("closed")
                          }}
                          className="flex w-full items-center justify-between px-3 py-1.5 transition hover:bg-white/10"
                        >
                          <span>Auto</span>
                          {selectedLevel === -1 && <CheckIcon className="h-3.5 w-3.5 text-error-600 dark:text-error-400" />}
                        </button>
                        {levels.map((level) => (
                          <button
                            key={level.index}
                            type="button"
                            onClick={() => {
                              setSelectedLevel(level.index)
                              if (hlsRef.current) hlsRef.current.currentLevel = level.index
                              setSettingsPane("closed")
                            }}
                            className="flex w-full items-center justify-between px-3 py-1.5 transition hover:bg-white/10"
                          >
                            <span>{level.label}</span>
                            {selectedLevel === level.index && <CheckIcon className="h-3.5 w-3.5 text-error-600 dark:text-error-400" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {pipSupported && (
              <button
                type="button"
                onClick={() => void togglePiP()}
                aria-label={isPiP ? "Salir de picture-in-picture" : "Picture-in-picture"}
                className="flex h-7 w-7 items-center justify-center rounded-full text-white transition hover:bg-white/10"
              >
                <PiPIcon className="h-4 w-4" />
              </button>
            )}

            <button
              type="button"
              onClick={() => void toggleFullscreen()}
              aria-label={isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"}
              className="flex h-7 w-7 items-center justify-center rounded-full text-white transition hover:bg-white/10"
            >
              {isFullscreen ? <MinimizeIcon className="h-4 w-4" /> : <MaximizeIcon className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
